import React, { useState, useMemo } from 'react';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const RulesTable = ({ 
  rules = [], 
  selectedRules = [], 
  onSelectRule, 
  onSelectAll, 
  onEditRule, 
  onDuplicateRule, 
  onToggleRuleStatus,
  onViewDetails 
}) => {
  const [expandedRule, setExpandedRule] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical':
        return 'text-error bg-error/10 border-error/20';
      case 'high':
        return 'text-error bg-error/10 border-error/20';
      case 'medium':
        return 'text-warning bg-warning/10 border-warning/20';
      case 'low':
        return 'text-success bg-success/10 border-success/20';
      default:
        return 'text-muted-foreground bg-muted border-border';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'text-success bg-success/10 border-success/20';
      case 'inactive':
        return 'text-muted-foreground bg-muted border-border';
      case 'pending':
        return 'text-warning bg-warning/10 border-warning/20';
      case 'deprecated':
        return 'text-error bg-error/10 border-error/20';
      default:
        return 'text-muted-foreground bg-muted border-border';
    }
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev?.key === key && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortedRules = React.useMemo(() => {
    if (!sortConfig?.key) return rules;
    
    return [...rules]?.sort((a, b) => {
      const aValue = a?.[sortConfig?.key];
      const bValue = b?.[sortConfig?.key];
      
      if (aValue < bValue) return sortConfig?.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig?.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [rules, sortConfig]);

  const toggleExpanded = (ruleId) => {
    setExpandedRule(expandedRule === ruleId ? null : ruleId);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const SortableHeader = ({ label, sortKey, className = '' }) => (
    <th 
      className={`px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground transition-micro ${className}`}
      onClick={() => handleSort(sortKey)}
    >
      <div className="flex items-center space-x-1">
        <span>{label}</span>
        <Icon 
          name={sortConfig?.key === sortKey && sortConfig?.direction === 'desc' ? 'ChevronDown' : 'ChevronUp'} 
          size={14}
          className={sortConfig?.key === sortKey ? 'text-primary' : 'text-muted-foreground/50'}
        />
      </div>
    </th>
  );

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-muted/30">
            <tr>
              <th className="px-6 py-3 text-left">
                <Checkbox
                  checked={selectedRules?.length === rules?.length && rules?.length > 0}
                  onChange={(e) => onSelectAll(e?.target?.checked)}
                />
              </th>
              <SortableHeader label="Rule Name" sortKey="name" />
              <SortableHeader label="Jurisdiction" sortKey="jurisdiction" />
              <SortableHeader label="Category" sortKey="category" />
              <SortableHeader label="Severity" sortKey="severity" />
              <SortableHeader label="Status" sortKey="status" />
              <SortableHeader label="Last Updated" sortKey="lastUpdated" />
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border">
            {sortedRules?.map((rule) => (
              <React.Fragment key={rule?.id}>
                <tr className="hover:bg-muted/20 transition-micro">
                  <td className="px-6 py-4">
                    <Checkbox
                      checked={selectedRules?.includes(rule?.id)}
                      onChange={(e) => onSelectRule(rule?.id, e?.target?.checked)}
                    />
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => toggleExpanded(rule?.id)}
                        className="text-muted-foreground hover:text-foreground transition-micro"
                      >
                        <Icon 
                          name={expandedRule === rule?.id ? 'ChevronDown' : 'ChevronRight'} 
                          size={16} 
                        />
                      </button>
                      <div>
                        <div className="text-sm font-medium text-foreground">{rule?.name}</div>
                        <div className="text-xs text-muted-foreground truncate max-w-xs">
                          {rule?.description}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-foreground">{rule?.jurisdiction}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-foreground">{rule?.category}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full border ${getSeverityColor(rule?.severity)}`}>
                      {rule?.severity}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(rule?.status)}`}>
                      {rule?.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">
                    {formatDate(rule?.lastUpdated)}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Edit"
                        onClick={() => onEditRule(rule)}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Copy"
                        onClick={() => onDuplicateRule(rule)}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName={rule?.status === 'active' ? 'Pause' : 'Play'}
                        onClick={() => onToggleRuleStatus(rule?.id)}
                      />
                    </div>
                  </td>
                </tr>
                
                {/* Expanded Details */}
                {expandedRule === rule?.id && (
                  <tr>
                    <td colSpan="8" className="px-6 py-4 bg-muted/10">
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <h4 className="text-sm font-medium text-foreground mb-2">Regulatory Text</h4>
                            <p className="text-sm text-muted-foreground bg-background p-3 rounded border">
                              {rule?.regulatoryText || 'No regulatory text provided.'}
                            </p>
                          </div>
                          <div>
                            <h4 className="text-sm font-medium text-foreground mb-2">AI Parameters</h4>
                            <div className="bg-background p-3 rounded border space-y-2">
                              <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">Context Weight:</span>
                                <span className="text-foreground">{rule?.aiParameters?.contextWeight || 0.7}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">Strict Mode:</span>
                                <span className="text-foreground">{rule?.aiParameters?.strictMode ? 'Yes' : 'No'}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">Auto-flag:</span>
                                <span className="text-foreground">{rule?.aiParameters?.autoFlag ? 'Yes' : 'No'}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <h4 className="text-sm font-medium text-foreground mb-2">Enforcement History</h4>
                            <div className="text-sm text-muted-foreground">
                              <div>Total Violations: {rule?.violations || 0}</div>
                              <div>This Month: {rule?.monthlyViolations || 0}</div>
                              <div>Avg. Severity: {rule?.avgSeverity || 'N/A'}</div>
                            </div>
                          </div>
                          <div>
                            <h4 className="text-sm font-medium text-foreground mb-2">Dates</h4>
                            <div className="text-sm text-muted-foreground">
                              <div>Effective: {formatDate(rule?.effectiveDate)}</div>
                              <div>Expires: {formatDate(rule?.expiryDate) || 'Never'}</div>
                              <div>Created: {formatDate(rule?.createdAt)}</div>
                            </div>
                          </div>
                          <div>
                            <h4 className="text-sm font-medium text-foreground mb-2">Citations</h4>
                            <div className="text-sm text-muted-foreground">
                              {rule?.citations?.length > 0 ? (
                                rule?.citations?.map((citation, index) => (
                                  <div key={index} className="truncate">{citation}</div>
                                ))
                              ) : (
                                <div>No citations available</div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile Cards */}
      <div className="lg:hidden">
        {sortedRules?.map((rule) => (
          <div key={rule?.id} className="border-b border-border last:border-b-0">
            <div className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-start space-x-3 flex-1">
                  <Checkbox
                    checked={selectedRules?.includes(rule?.id)}
                    onChange={(e) => onSelectRule(rule?.id, e?.target?.checked)}
                  />
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-foreground mb-1">{rule?.name}</h3>
                    <p className="text-xs text-muted-foreground mb-2">{rule?.description}</p>
                    <div className="flex flex-wrap gap-2 mb-2">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full border ${getSeverityColor(rule?.severity)}`}>
                        {rule?.severity}
                      </span>
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(rule?.status)}`}>
                        {rule?.status}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {rule?.jurisdiction} • {rule?.category} • Updated {formatDate(rule?.lastUpdated)}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => toggleExpanded(rule?.id)}
                  className="text-muted-foreground hover:text-foreground transition-micro min-touch-target"
                >
                  <Icon 
                    name={expandedRule === rule?.id ? 'ChevronDown' : 'ChevronRight'} 
                    size={20} 
                  />
                </button>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="text-xs text-muted-foreground">
                  {rule?.violations || 0} violations this month
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Edit"
                    onClick={() => onEditRule(rule)}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Copy"
                    onClick={() => onDuplicateRule(rule)}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName={rule?.status === 'active' ? 'Pause' : 'Play'}
                    onClick={() => onToggleRuleStatus(rule?.id)}
                  />
                </div>
              </div>
            </div>
            
            {/* Mobile Expanded Details */}
            {expandedRule === rule?.id && (
              <div className="px-4 pb-4 bg-muted/10 border-t border-border">
                <div className="space-y-3 pt-3">
                  <div>
                    <h4 className="text-sm font-medium text-foreground mb-1">Regulatory Text</h4>
                    <p className="text-sm text-muted-foreground bg-background p-2 rounded border">
                      {rule?.regulatoryText || 'No regulatory text provided.'}
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <h4 className="text-sm font-medium text-foreground mb-1">AI Parameters</h4>
                      <div className="text-xs text-muted-foreground space-y-1">
                        <div>Weight: {rule?.aiParameters?.contextWeight || 0.7}</div>
                        <div>Strict: {rule?.aiParameters?.strictMode ? 'Yes' : 'No'}</div>
                        <div>Auto-flag: {rule?.aiParameters?.autoFlag ? 'Yes' : 'No'}</div>
                      </div>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-foreground mb-1">Dates</h4>
                      <div className="text-xs text-muted-foreground space-y-1">
                        <div>Effective: {formatDate(rule?.effectiveDate)}</div>
                        <div>Expires: {formatDate(rule?.expiryDate) || 'Never'}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default RulesTable;